import { c as _c } from "react/compiler-runtime";
import { useQuery, UseQueryResult } from 'react-query';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '@/components/AuthProvider';
import * as api from '@/utils/api.js';
interface UseMagicLinkRedirectOptions {
  enabled?: boolean;
  redirectPath?: string;
  /** react-query's `retry` (default: its own, three retries). */
  retry?: boolean | number;
  /** Refetch a failed query when the window regains focus (default: true). */
  refetchOnWindowFocus?: boolean;
  /** Refetch a failed query when the network reconnects (default: true). */
  refetchOnReconnect?: boolean;
}
export function magicLinkQueryKey(redirectPath: string, email?: string | null): readonly ['magic-link', string, string | null] {
  return ['magic-link', redirectPath, email ?? null];
}
export function isMagicLinkQueryEnabled(enabled: boolean, email?: string | null): boolean {
  return enabled && Boolean(email);
}

/**
 * Hook to fetch a Campuswire magic link with PostHog tracking parameters
 * @param options.enabled - Whether the query should run (default: true)
 * @param options.redirectPath - The redirect path to use (default: '/c/portal')
 * @param options.retry - How often a failed fetch retries (react-query's default when omitted)
 * @param options.refetchOnWindowFocus - Whether focus refetches (react-query's default when omitted)
 * @param options.refetchOnReconnect - Whether a reconnect refetches (react-query's default when omitted)
 * @returns UseQueryResult containing the magic link URL
 */
export function useMagicLinkRedirect(t0) {
  const $ = _c(24);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    enabled: t2,
    redirectPath: t3,
    retry,
    refetchOnWindowFocus,
    refetchOnReconnect
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  const redirectPath = t3 === undefined ? "/c/portal" : t3;
  const posthog = usePostHog();
  const {
    email
  } = useAuth();
  let t4;
  if ($[2] !== email || $[3] !== redirectPath) {
    t4 = magicLinkQueryKey(redirectPath, email);
    $[2] = email;
    $[3] = redirectPath;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== posthog || $[6] !== redirectPath) {
    t5 = () => {
      const session_id = posthog?.get_session_id();
      const distinct_id = posthog?.get_distinct_id();
      return api.getCampuswireMagicLink(redirectPath).then(res => {
        let magicLink = res.data.link;
        if (session_id && distinct_id) {
          const url = new URL(magicLink);
          const hashParams = new URLSearchParams(url.hash.substring(1));
          hashParams.set("session_id", session_id);
          hashParams.set("distinct_id", distinct_id);
          url.hash = hashParams.toString();
          magicLink = url.toString();
        }
        return magicLink;
      });
    };
    $[5] = posthog;
    $[6] = redirectPath;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== email || $[9] !== enabled) {
    t6 = isMagicLinkQueryEnabled(enabled, email);
    $[8] = email;
    $[9] = enabled;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== retry) {
    t7 = retry !== undefined && {
      retry
    };
    $[11] = retry;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== refetchOnWindowFocus) {
    t8 = refetchOnWindowFocus !== undefined && {
      refetchOnWindowFocus
    };
    $[13] = refetchOnWindowFocus;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== refetchOnReconnect) {
    t9 = refetchOnReconnect !== undefined && {
      refetchOnReconnect
    };
    $[15] = refetchOnReconnect;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = {
      queryKey: t4,
      queryFn: t5,
      staleTime: Infinity,
      enabled: t6,
      ...t7,
      ...t8,
      ...t9
    };
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return useQuery(t10);
}